import React from "react";
import { graphql, useFragment } from "react-relay";

import { faBook, faGlobeAmericas } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import perf from "~/lib/perf";
import MeetingStreamLink from "~/nav/panels/Home/Links/MeetingStreamLink";
import MenuLink, { type MenuLinkProps } from "~/nav/panels/Home/Links/MenuLink";
import OneOnOneLink from "~/nav/panels/Home/Links/OneOnOneLink";
import { ConnectedIcon } from "~/stream/ConnectedIcon";

import GroupsMenu from "./GroupsMenu";

import type { StreamLink_stream$key as StreamFragment } from "~/__generated__/StreamLink_stream.graphql";

import styles from "~/nav/panels/shared/PanelSection.module.css";

const publicIcon = <FontAwesomeIcon icon={faGlobeAmericas} fixedWidth />;
const bookIcon = <FontAwesomeIcon icon={faBook} fixedWidth />;

export default function StreamLink(
	props: Omit<MenuLinkProps, "to" | "innerRef" | "activeClassName"> & {
		stream: StreamFragment;
		innerRef?: React.Ref<HTMLAnchorElement>;
	},
) {
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);

	const { stream: streamProp, innerRef, ...restProps } = props;
	const stream = useFragment(
		graphql`
			fragment StreamLink_stream on NoteStream {
				id
				type
				url
				displayName
				userCount
				viewerLastSeenAt
				latestNoteEditedAt
				isExternal
				hasExternalUsers
				...GroupsMenu_stream
				otherUser {
					...OneOnOneLink_user
				}
				linkedEvent {
					id
					guid
					nextOccurrence {
						start
						end
						...RecordIcon_event
					}
					finalOccurrence
				}
				latestNote {
					id
					eventStart
					eventEnd
					finished
					event {
						id
						guid
						...RecordIcon_event
					}
				}
				publicLink {
					enabled
				}
			}
		`,
		streamProp,
	);

	const { onClick: propsOnClick } = restProps;
	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			perf.startSwitchingStreams("streams");
			return propsOnClick?.(e);
		},
		[propsOnClick],
	);

	if (!stream) return null;
	// The stream has unread changes if the user has already seen it (otherwise
	// it gets a "New" badge) and it has been updated since then.
	const unread = Boolean(
		stream.latestNoteEditedAt && moment(stream.viewerLastSeenAt).isBefore(stream.latestNoteEditedAt),
	);

	let badge;
	if (stream.latestNoteEditedAt && !stream.viewerLastSeenAt) {
		badge = <span className={cx(styles.badge, styles.badgeTeal)}>new</span>;
	}

	if (stream.type === "oneonone") {
		if (stream.otherUser) {
			return (
				<OneOnOneLink
					stream={stream}
					user={stream.otherUser}
					{...restProps}
					innerRef={innerRef}
					badge={badge}
					unread={unread}
					streamId={stream.id}
					onClick={onClick}
				/>
			);
		}
		// Without an `otherUser`, we can't link to a 1on1, so until we can, we don't show the button at all.
		return null;
	} else if (stream.type === "meeting") {
		return <MeetingStreamLink stream={stream} {...restProps} innerRef={innerRef} unread={unread} onClick={onClick} />;
	} else {
		const { url, displayName, userCount, publicLink } = stream;
		const title = displayName !== "" ? displayName : "Untitled note series";
		const iconTooltip = publicLink?.enabled
			? "Publicly shared"
			: userCount > 1
				? "Shared with teammates"
				: "Private to me";
		return (
			<div className={styles.wrapper}>
				{/* Technically stream URL is nullable, but theoretically that should never apply to 1on1 or Personal streams. If
				it turns out to be null, this has a best-effort try at it. */}
				<MenuLink
					{...restProps}
					innerRef={innerRef}
					to={url ?? `/stream/${stream.id}/`}
					onClick={onClick}
					streamId={stream.id}
					icon={publicLink?.enabled ? publicIcon : bookIcon}
					iconTooltip={iconTooltip}
					badge={badge}
					unread={unread}
					activeClassName={styles.active}
					className={cx({ [styles.untitledStream]: displayName === "" })}
					data-test-id="stream-link"
				>
					<span title={title} className={styles.title}>
						{(stream.isExternal || stream.hasExternalUsers) && <ConnectedIcon className={styles.connectedIcon} />}
						{title}
					</span>
				</MenuLink>
				<GroupsMenu stream={stream} isMenuOpen={isMenuOpen} toggleOpen={setIsMenuOpen} />
			</div>
		);
	}
}
